// src/components/AdminReports.js
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../i18n/i18n';
import {
  Box, Typography, Paper, Button, TextField, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, CircularProgress,
  Divider, useTheme, useMediaQuery, Tooltip,
  Select, MenuItem, FormControl, InputLabel, OutlinedInput, Avatar
} from '@mui/material';
import { motion } from 'framer-motion';
import API_ENDPOINTS from '../config/apiConfig';
import FixedSnackbar from './FixedSnackbar';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';

const STATUS_CONFIG = {
  pending: { color: 'warning', icon: <PendingIcon fontSize="small" /> },
  reviewed: { color: 'info', icon: <ScheduleIcon fontSize="small" /> },
  resolved: { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  deleted: { color: 'error', icon: <DeleteSweepIcon fontSize="small" /> },
};

function AdminReports({ role }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isSuperadmin = role === 'superadmin';

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [resolutionDialogOpen, setResolutionDialogOpen] = useState(false);
  const [resolutionText, setResolutionText] = useState('');
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  const fetchReports = useCallback(async (statusFilter = 'all') => {
    setLoading(true);
    try {
      const url = statusFilter === 'all'
        ? API_ENDPOINTS.REPORTS_LIST
        : `${API_ENDPOINTS.REPORTS_LIST}?status=${statusFilter}`;
      const response = await fetch(url, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setReports(data);
      } else {
        setSnackbar({ open: true, message: t('report_error_load_failed'), severity: 'error' });
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      setSnackbar({ open: true, message: t('report_error_generic'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchReports(filterStatus);
  }, [fetchReports, filterStatus]);

  const handleDeleteClick = (report) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reportToDelete) return;
    setDeleting(true);
    try {
      const response = await fetch(API_ENDPOINTS.REPORTS_DELETE(reportToDelete._id), {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        setSnackbar({ open: true, message: t('report_success_deleted'), severity: 'success' });
        fetchReports(filterStatus);
        setDeleteDialogOpen(false);
        setReportToDelete(null);
        setDetailOpen(false);
      } else {
        const data = await response.json();
        setSnackbar({ open: true, message: data.message || t('report_error_delete_failed'), severity: 'error' });
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      setSnackbar({ open: true, message: t('report_error_generic'), severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (reportId, newStatus) => {
    if (newStatus === 'resolved') {
      setPendingStatusChange({ reportId, status: newStatus });
      setResolutionDialogOpen(true);
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.REPORTS_UPDATE_STATUS(reportId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        setSnackbar({ open: true, message: t('report_success_status_updated'), severity: 'success' });
        fetchReports(filterStatus);
        setSelectedReport(prev => prev && prev._id === reportId ? { ...prev, status: newStatus } : prev);
      } else {
        const data = await response.json();
        setSnackbar({ open: true, message: data.message || t('report_error_status_update'), severity: 'error' });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({ open: true, message: t('report_error_generic'), severity: 'error' });
    }
  };

  const handleResolutionConfirm = async () => {
    if (!pendingStatusChange || !resolutionText.trim()) return;
    try {
      const response = await fetch(API_ENDPOINTS.REPORTS_UPDATE_STATUS(pendingStatusChange.reportId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: 'resolved', resolution: resolutionText.trim() }),
      });
      if (response.ok) {
        setSnackbar({ open: true, message: t('report_success_resolved'), severity: 'success' });
        fetchReports(filterStatus);
        setResolutionDialogOpen(false);
        setResolutionText('');
        setPendingStatusChange(null);
        setSelectedReport(null);
        setDetailOpen(false);
      } else {
        const data = await response.json();
        setSnackbar({ open: true, message: data.message || t('report_error_status_update'), severity: 'error' });
      }
    } catch (error) {
      console.error('Error resolving report:', error);
      setSnackbar({ open: true, message: t('report_error_generic'), severity: 'error' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const locale = 'es-MX';
    return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const filteredReports = reports.filter((report) => {
    if (filterStatus === 'deleted') {
      if (!report.deleted) return false;
    } else if (filterStatus !== 'all') {
      if (report.status !== filterStatus || report.deleted) return false;
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return (
        (report.subject || '').toLowerCase().includes(q) ||
        (report.description || '').toLowerCase().includes(q) ||
        (report.location || '').toLowerCase().includes(q) ||
        (report.submitted_by || '').toLowerCase().includes(q) ||
        (report.submitted_by_name || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const stats = {
    total: reports.length,
    pending: reports.filter(r => r.status === 'pending').length,
    reviewed: reports.filter(r => r.status === 'reviewed').length,
    resolved: reports.filter(r => r.status === 'resolved').length,
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 } }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: { xs: 3, sm: 4 } }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {t('admin_reports_title') || 'Reports'}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640 }}>
              {t('admin_reports_subtitle') || 'Review and manage confidential workplace reports.'}
            </Typography>
          </Box>
          <Tooltip title={t('report_refresh')}>
            <IconButton onClick={() => fetchReports(filterStatus)} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Stats Cards */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: t('report_stat_total'), value: stats.total, color: 'default' },
            { label: t('report_stat_pending'), value: stats.pending, color: 'warning' },
            { label: t('report_stat_reviewed'), value: stats.reviewed, color: 'info' },
            { label: t('report_stat_resolved'), value: stats.resolved, color: 'success' },
          ].map((stat, idx) => (
            <Paper key={idx} elevation={0} sx={{ flex: '1 1 100px', minWidth: 100, p: 2, borderRadius: 2, border: 1, borderColor: 'divider', textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: stat.color === 'default' ? 'text.primary' : `${stat.color}.main` }}>
                {stat.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stat.label}
              </Typography>
            </Paper>
          ))}
        </Box>

        {/* Filters */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FilterListIcon sx={{ color: '#62B6CB' }} />
            <TextField
              size="small"
              placeholder={t('report_search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 200 }, flex: 1, maxWidth: 350 }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 140 } }}>
              <InputLabel>{t('report_filter_status')}</InputLabel>
              <Select
                value={filterStatus}
                label={t('report_filter_status')}
                onChange={(e) => setFilterStatus(e.target.value)}
                input={<OutlinedInput label={t('report_filter_status')} />}
              >
                <MenuItem value="all">{t('report_filter_all')}</MenuItem>
                <MenuItem value="pending">{t('report_status_pending')}</MenuItem>
                <MenuItem value="reviewed">{t('report_status_reviewed')}</MenuItem>
                <MenuItem value="resolved">{t('report_status_resolved')}</MenuItem>
                <MenuItem value="deleted">{t('report_status_deleted')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* Reports List */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('report_loading')}
            </Typography>
          </Box>
        ) : filteredReports.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 2, border: 1, borderColor: 'divider' }}>
            <Avatar sx={{ width: 64, height: 64, mb: 2, bgcolor: 'grey.100', color: 'grey.400', mx: 'auto' }}>
              <ReportProblemIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t('report_empty')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No reports match your current filters.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filteredReports.map((report) => {
              const statusConf = STATUS_CONFIG[report.status] || STATUS_CONFIG.pending;
              return (
                <Paper
                  key={report._id}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': {
                      borderColor: 'primary.light',
                      boxShadow: 1,
                    },
                  }}
                  onClick={() => { setSelectedReport(report); setDetailOpen(true); }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          icon={statusConf.icon}
                          label={t(`report_status_${report.status}`)}
                          size="small"
                          color={statusConf.color}
                          sx={{ fontWeight: 500 }}
                        />
                        {report.deleted && (
                          <Chip
                            icon={<DeleteSweepIcon fontSize="small" />}
                            label={t('report_status_deleted')}
                            size="small"
                            color="error"
                            sx={{ fontWeight: 500 }}
                          />
                        )}
                      </Box>
                      {report.subject && (
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
                          {report.subject}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {report.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {formatDate(report.created_at)}
                        {report.location && ` • ${report.location}`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip title={t('report_view_details')}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedReport(report); setDetailOpen(true); }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isSuperadmin && !report.deleted && (
                        <Tooltip title={t('report_delete')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => { e.stopPropagation(); handleDeleteClick(report); }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}

        {/* Summary */}
        {!loading && reports.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'right' }}>
            {filteredReports.length === reports.length
              ? t('report_total_count', { count: reports.length })
              : t('report_filtered_count', { filtered: filteredReports.length, total: reports.length })}
          </Typography>
        )}
      </motion.div>

      {/* Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isSmDown}
      >
        {selectedReport && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={STATUS_CONFIG[selectedReport.status]?.icon}
                  label={t(`report_status_${selectedReport.status}`)}
                  size="small"
                  color={STATUS_CONFIG[selectedReport.status]?.color || 'default'}
                  sx={{ fontWeight: 500 }}
                />
                {selectedReport.deleted && (
                  <Chip
                    icon={<DeleteSweepIcon fontSize="small" />}
                    label={t('report_status_deleted')}
                    size="small"
                    color="error"
                    sx={{ fontWeight: 500 }}
                  />
                )}
              </Box>
              <IconButton onClick={() => setDetailOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Subject */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', mb: 0.5 }}>
                    {t('report_field_subject')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedReport.subject || '-'}
                  </Typography>
                </Box>

                {/* Description */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', mb: 0.5 }}>
                    {t('report_field_description')}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {selectedReport.description}
                  </Typography>
                </Box>

                {/* Location */}
                {selectedReport.location && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', mb: 0.5 }}>
                      {t('report_field_location')}
                    </Typography>
                    <Typography variant="body2">{selectedReport.location}</Typography>
                  </Box>
                )}

                {/* Submitted by - superadmin only */}
                {isSuperadmin && (selectedReport.submitted_by || selectedReport.submitted_by_name) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', mb: 0.5 }}>
                      {t('report_submitted_by')}
                    </Typography>
                    <Typography variant="body2">
                      {selectedReport.submitted_by_name
                        ? `${selectedReport.submitted_by_name} (${selectedReport.submitted_by})`
                        : selectedReport.submitted_by}
                    </Typography>
                  </Box>
                )}

                {/* Resolution */}
                {selectedReport.resolution && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', mb: 0.5 }}>
                      {t('report_resolution_label')}
                    </Typography>
                    <Typography variant="body2">{selectedReport.resolution}</Typography>
                  </Box>
                )}

                <Divider />

                {/* Dates */}
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('report_created')}: {formatDate(selectedReport.created_at)}
                  </Typography>
                  {selectedReport.updated_at && selectedReport.updated_at !== selectedReport.created_at && (
                    <Typography variant="caption" color="text.secondary">
                      {t('report_updated')}: {formatDate(selectedReport.updated_at)}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Status Change */}
              {!selectedReport.deleted && selectedReport.status !== 'resolved' && (
                <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                    {t('report_change_status')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['pending', 'reviewed', 'resolved'].map(status => {
                      const isActive = selectedReport.status === status;
                      const conf = STATUS_CONFIG[status];
                      return (
                        <Chip
                          key={status}
                          label={t(`report_status_${status}`)}
                          size="small"
                          onClick={() => handleStatusChange(selectedReport._id, status)}
                          color={isActive ? conf.color : 'default'}
                          sx={{
                            cursor: 'pointer',
                            fontWeight: isActive ? 600 : 400,
                            '&:hover': { opacity: 0.8 }
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
              <Button onClick={() => setDetailOpen(false)} sx={{ textTransform: 'none' }}>
                {t('report_close')}
              </Button>
              {isSuperadmin && !selectedReport.deleted && (
                <Button
                  color="error"
                  onClick={() => handleDeleteClick(selectedReport)}
                  startIcon={<DeleteIcon />}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {t('report_delete')}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Resolution Dialog */}
      <Dialog open={resolutionDialogOpen} onClose={() => { setResolutionDialogOpen(false); setPendingStatusChange(null); setResolutionText(''); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('report_resolution_dialog_title')}
          </Typography>
          <IconButton onClick={() => { setResolutionDialogOpen(false); setPendingStatusChange(null); setResolutionText(''); }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('report_resolution_dialog_description')}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('report_resolution_label')}
            value={resolutionText}
            onChange={(e) => setResolutionText(e.target.value)}
            placeholder={t('report_resolution_placeholder')}
            inputProps={{ maxLength: 2000 }}
            helperText={`${resolutionText.length}/2000`}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => { setResolutionDialogOpen(false); setPendingStatusChange(null); setResolutionText(''); }} sx={{ textTransform: 'none' }}>
            {t('report_cancel')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleResolutionConfirm}
            disabled={!resolutionText.trim()}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {t('report_confirm_resolve')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('report_confirm_delete_title')}
          </Typography>
          <IconButton onClick={() => setDeleteDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>{t('report_confirm_delete_message')}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting} sx={{ textTransform: 'none' }}>
            {t('report_cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {t('report_confirm_delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <FixedSnackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}

export default AdminReports;
